import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, CheckCircle, Upload, FileText, CreditCard, Car, Camera, Gift, Users, Copy } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DeliveryKYC = () => {
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  // KYC Documents
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState('');
  const [aadharFront, setAadharFront] = useState(null);
  const [aadharFrontPreview, setAadharFrontPreview] = useState('');
  const [aadharBack, setAadharBack] = useState(null);
  const [aadharBackPreview, setAadharBackPreview] = useState('');
  const [panCard, setPanCard] = useState(null);
  const [panCardPreview, setPanCardPreview] = useState('');
  const [drivingLicense, setDrivingLicense] = useState(null);
  const [drivingLicensePreview, setDrivingLicensePreview] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    aadhar_number: '',
    pan_number: '',
    vehicle_type: '',
    vehicle_number: '',
  });

  useEffect(() => {
    const storedPartner = localStorage.getItem('fudco_delivery_partner');
    if (!storedPartner) {
      toast.error('Please complete delivery partner registration first');
      navigate('/delivery-partner');
      return;
    }
    const partnerData = JSON.parse(storedPartner);
    setPartner(partnerData);
    // Generate referral code
    setReferralCode(`FUDCO${partnerData.phone?.slice(-4) || 'DLVR'}${Math.random().toString(36).substring(2, 6).toUpperCase()}`);
  }, [navigate]);

  const handleChange = (e) => {
    const value = e.target.value.toUpperCase();
    const name = e.target.name;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    const preview = URL.createObjectURL(file);

    switch(type) {
      case 'profilePhoto':
        setProfilePhoto(file);
        setProfilePhotoPreview(preview);
        break;
      case 'aadharFront':
        setAadharFront(file);
        setAadharFrontPreview(preview);
        break;
      case 'aadharBack':
        setAadharBack(file);
        setAadharBackPreview(preview);
        break;
      case 'panCard':
        setPanCard(file);
        setPanCardPreview(preview);
        break;
      case 'drivingLicense':
        setDrivingLicense(file);
        setDrivingLicensePreview(preview);
        break;
      default:
        break;
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Referral code copied!');
  };

  const handleSubmit = async () => {
    if (!profilePhoto) {
      toast.error('Profile photo is required');
      return;
    }
    if (!aadharFront || !aadharBack) {
      toast.error('Both sides of Aadhar card are required');
      return;
    }
    if (!panCard) {
      toast.error('PAN card is required');
      return;
    }
    if (!drivingLicense) {
      toast.error('Driving license is required');
      return;
    }
    
    if (formData.aadhar_number.length !== 12) {
      toast.error('Please enter valid 12-digit Aadhar number');
      return;
    }
    if (formData.pan_number.length !== 10) {
      toast.error('Please enter valid 10-character PAN number');
      return;
    }
    if (!formData.vehicle_type) {
      toast.error('Please select vehicle type');
      return;
    }
    if (!formData.vehicle_number) {
      toast.error('Please enter vehicle number');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/partners/delivery/kyc`, {
        partner_id: partner.id,
        ...formData,
        referral_code: referralCode,
        profile_photo: profilePhoto?.name || '',
        aadhar_front: aadharFront?.name || '',
        aadhar_back: aadharBack?.name || '',
        pan_card: panCard?.name || '',
        driving_license: drivingLicense?.name || '',
      });

      toast.success('KYC submitted successfully!');
      localStorage.removeItem('fudco_delivery_partner');
      
      // Show success modal
      setShowSuccessModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit KYC. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!partner) {
    return null;
  }

  const DocumentUpload = ({ label, icon: Icon, file, preview, type, required = true }) => (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#FFD700]" />
        {label} {required && '*'}
      </Label>
      <div 
        className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all hover:border-[#FFD700] ${
          file ? 'border-[#FFD700] bg-yellow-50' : 'border-gray-300'
        }`}
        onClick={() => document.getElementById(type).click()}
      >
        {preview ? (
          <div className="space-y-2">
            <img 
              src={preview} 
              alt={label} 
              className="w-full h-24 object-cover rounded-lg mx-auto"
            />
            <p className="text-xs text-[#2D8B2E] font-medium truncate">{file?.name}</p>
          </div>
        ) : (
          <div className="space-y-1">
            <Upload className="w-8 h-8 text-gray-400 mx-auto" />
            <p className="text-xs text-gray-500">Click to upload</p>
          </div>
        )}
      </div>
      <input
        type="file"
        id={type}
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFileChange(e, type)}
        data-testid={`${type}-input`}
      />
    </div>
  );

  return (
    <div className="min-h-screen pt-20 bg-gray-50" data-testid="delivery-kyc-page">
      {/* Header */}
      <section className="bg-white border-b border-gray-200 py-6">
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={() => navigate('/delivery-partner')}
            className="flex items-center gap-2 text-gray-600 hover:text-[#FFD700] mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex items-center gap-4">
            <div className="bg-[#FFD700] text-black p-3 rounded-2xl">
              <FileText className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                KYC Verification
              </h1>
              <p className="text-gray-600">{partner.name}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Progress Indicator */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] text-black flex items-center justify-center">
                <CheckCircle className="w-5 h-5" />
              </div>
              <span className="text-sm font-medium text-[#2D8B2E]">Registration</span>
            </div>
            <div className="flex-1 h-1 bg-[#FFD700] rounded"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] text-black flex items-center justify-center">
                <CheckCircle className="w-5 h-5" />
              </div>
              <span className="text-sm font-medium text-[#2D8B2E]">Verification</span>
            </div>
            <div className="flex-1 h-1 bg-[#FFD700] rounded"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] text-black flex items-center justify-center font-semibold">
                3
              </div>
              <span className="text-sm font-medium">KYC Documents</span>
            </div>
          </div>
        </div>
      </div>

      {/* KYC Form */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Profile Photo */}
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Profile Photo
            </h2>
            <div className="flex justify-center">
              <div className="w-40">
                <DocumentUpload 
                  label="Your Photo" 
                  icon={Camera} 
                  file={profilePhoto} 
                  preview={profilePhotoPreview} 
                  type="profilePhoto" 
                />
              </div>
            </div>
          </Card>

          {/* Identity Documents */}
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Identity Documents
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <DocumentUpload 
                label="Aadhar Card (Front)" 
                icon={CreditCard} 
                file={aadharFront} 
                preview={aadharFrontPreview} 
                type="aadharFront" 
              />
              <DocumentUpload 
                label="Aadhar Card (Back)" 
                icon={CreditCard} 
                file={aadharBack} 
                preview={aadharBackPreview} 
                type="aadharBack" 
              />
            </div>
            
            <div className="space-y-2 mb-6">
              <Label htmlFor="aadhar_number">Aadhar Number *</Label>
              <Input
                id="aadhar_number"
                name="aadhar_number"
                value={formData.aadhar_number}
                onChange={handleChange}
                placeholder="12-digit Aadhar number"
                maxLength={12}
                className="h-12 rounded-xl"
                data-testid="input-aadhar"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <DocumentUpload 
                label="PAN Card" 
                icon={CreditCard} 
                file={panCard} 
                preview={panCardPreview} 
                type="panCard" 
              />
              <div className="space-y-2">
                <Label htmlFor="pan_number">PAN Number *</Label>
                <Input
                  id="pan_number"
                  name="pan_number"
                  value={formData.pan_number}
                  onChange={handleChange}
                  placeholder="e.g., ABCDE1234F"
                  maxLength={10}
                  className="h-12 rounded-xl"
                  data-testid="input-pan"
                />
              </div>
            </div>
          </Card>

          {/* Vehicle Documents */}
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Vehicle Documents
            </h2>
            
            <div className="mb-6">
              <DocumentUpload 
                label="Driving License" 
                icon={Car} 
                file={drivingLicense} 
                preview={drivingLicensePreview} 
                type="drivingLicense" 
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Vehicle Type *</Label>
                <Select onValueChange={(value) => setFormData({ ...formData, vehicle_type: value })} value={formData.vehicle_type}>
                  <SelectTrigger className="h-12 rounded-xl" data-testid="select-vehicle-type">
                    <SelectValue placeholder="Select vehicle type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bicycle">Bicycle</SelectItem>
                    <SelectItem value="two_wheeler">Two Wheeler (Bike/Scooter)</SelectItem>
                    <SelectItem value="ev_two_wheeler">EV Two Wheeler</SelectItem>
                    <SelectItem value="three_wheeler">Three Wheeler (Auto)</SelectItem>
                    <SelectItem value="four_wheeler">Four Wheeler (Car)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="vehicle_number">Vehicle Number *</Label>
                <Input
                  id="vehicle_number"
                  name="vehicle_number"
                  value={formData.vehicle_number}
                  onChange={handleChange}
                  placeholder="e.g., OD 02 AB 1234"
                  className="h-12 rounded-xl"
                  data-testid="input-vehicle-number"
                />
              </div>
            </div>
          </Card>

          {/* Refer & Earn Section */}
          <Card className="p-6 mb-6 bg-gradient-to-br from-yellow-50 to-white border-yellow-200" data-testid="refer-earn-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#FFD700] text-black p-3 rounded-2xl">
                <Gift className="w-6 h-6" />
              </div>
              <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Refer a Friend & Earn
              </h2>
            </div>
            
            <p className="text-gray-600 mb-4">
              Share your referral code and earn rewards when your friends join Fudco!
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white p-4 rounded-xl border border-yellow-200">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-[#FFD700]" />
                  <span className="font-semibold">Refer Delivery Partner</span>
                </div>
                <p className="text-2xl font-bold text-[#2D8B2E]">₹50 <span className="text-sm font-normal text-gray-500">per referral</span></p>
              </div>
              <div className="bg-white p-4 rounded-xl border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-[#2D8B2E]" />
                  <span className="font-semibold">Refer Restaurant</span>
                </div>
                <p className="text-2xl font-bold text-[#2D8B2E]">₹50 <span className="text-sm font-normal text-gray-500">per referral</span></p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-1 bg-gray-100 px-4 py-3 rounded-xl font-mono text-lg font-bold text-[#2D8B2E]">
                {referralCode}
              </div>
              <Button 
                onClick={copyReferralCode}
                className="bg-[#FFD700] hover:bg-[#e6c200] text-black px-6"
                data-testid="copy-referral-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>
          </Card>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full h-14 bg-[#FFD700] hover:bg-[#e6c200] text-black rounded-full font-semibold text-lg"
            data-testid="submit-kyc-btn"
          >
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              'Submit KYC & Complete Registration'
            )}
          </Button>
        </div>
      </section>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md" data-testid="success-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Thank You!
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4 text-center">
            <div className="bg-yellow-100 text-yellow-700 p-6 rounded-full inline-block">
              <CheckCircle className="w-12 h-12" />
            </div>
            <p className="text-gray-700 text-lg font-medium">
              We will reach out to you soon when we go Live with our Application!
            </p>
            <p className="text-gray-500 text-sm">
              Our team will contact you on your registered phone number.
            </p>
            <Button
              onClick={() => {
                setShowSuccessModal(false);
                navigate('/');
              }}
              className="w-full h-12 bg-[#FFD700] hover:bg-[#e6c200] text-black rounded-full font-semibold"
              data-testid="success-close-btn"
            >
              Back to Home
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
